/* eslint-disable react/no-array-index-key */
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    NSTable,
    NSCellInput,
} from 'bricks';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import RenderIf from 'components/RenderIf/RenderIf';
import { formatISOStartOfMonth } from 'ns_libs/formatter';
import { OPERATIONS_INCOME_WS_HANDLERS, CASH_FLOW_TOOLTIP_MESSAGE, WattsType } from 'views/ProFormaTable/constants';
import { OtherIncome, IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import { ProFormaModelIds, IncomeCalculationType, PRO_FORMA_MODELS_WITH_LEASING_COST_RESERVES } from 'constants/proForma';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import PeriodsSelect from '../../../../PeriodsSelect/PeriodsSelect';
import { ColumnKeys, COLUMN_KEYS_BY_MODEL_ID } from './constants';
import './OtherIncomeTable.scss';

export interface IOtherIncomeTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
    monthlyPeriodOptions?: IFormattedCashFlowPeriod[];
}

const OtherIncomeTable = ({
    operationBeginIndex, baseCashFlowColumns, monthlyPeriodOptions = [],
}: IOtherIncomeTableProps) => {
    const {
        incomeSummary, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma, operatingInfo, wattsType,
    } = useProFormaSocketContext();

    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);
    const isCommercial = PRO_FORMA_MODELS_WITH_LEASING_COST_RESERVES.includes(proForma.proFormaModelId);
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const areaType = isApartment || !isIncomeCalculationTypeEstimate || isCommercial ? 'RSF' : 'SF';

    const handleUpdateOtherIncome = (id: number, field: string) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.OTHER_INCOME.UPDATE, id, field);

    const otherIncomeCashFlowColumns: ColumnDef<OtherIncome>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `other-income-${column.id}`,
        accessorFn: (row, otherIncomeIndex: number) => {
            let hasCashFlow = typeof operationsCashFlow?.[operationBeginIndex]?.otherIncome?.[otherIncomeIndex]?.value === 'number';
            if (!isApartment && !isIncomeCalculationTypeEstimate) {
                hasCashFlow = Boolean(row.startMonth && typeof operationsCashFlow?.[row.startMonth]?.otherIncome?.[otherIncomeIndex]?.value === 'number');
            }
            return (
                hasCashFlow
                    ? (operationsCashFlow?.[cashFlowIndex]?.otherIncome?.[otherIncomeIndex]?.value || 0)
                    : null
            );
        },
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                <span className="text-dark">
                    {formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalOtherIncome || 0, showCents, 'currency')}
                </span>
            </RenderIf>
        ),
    }));

    // eslint-disable-next-line no-unused-vars
    const COLUMNS_BY_KEY: { [key in ColumnKeys]: ColumnDef<OtherIncome> } = {
        [ColumnKeys.START_DATE]: {
            accessorKey: 'startDate',
            id: 'startDate',
            header: () => (
                <RenderIf isTrue={!isIncomeCalculationTypeEstimate}>
                    <span>Start</span>
                </RenderIf>
            ),
            cell: ({ row, getValue }) => (
                <RenderIf isTrue={!isIncomeCalculationTypeEstimate}>
                    <PeriodsSelect
                        compact
                        hasInvertedColors
                        handleChange={value => {
                            const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'startDate');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        options={monthlyPeriodOptions}
                        value={!getValue() ? null
                            : formatISOStartOfMonth(getValue() as string)}
                    />
                </RenderIf>
            ),
        },
        [ColumnKeys.ANNUAL_GROWTH]: {
            accessorKey: 'annualGrowth',
            id: 'annualGrowth',
            header: () => <span>Annual growth</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'annualGrowth');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.totalAnnualGrowth || 0, true, 'percentage')}</span>,
        },
        [ColumnKeys.PERCENT_OF_TOTAL_RENT]: {
            accessorKey: 'percentOfTotalRent',
            id: 'percentOfTotalRent',
            header: () => (
                <span>
                    {isApartment ? '% of total rent' : '% of rental income'}
                </span>
            ),
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'percentOfTotalRent');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.percentOfTotalRent || 0, true, 'percentage')}</span>,
        },
        [ColumnKeys.PRICE_PER_SF]: {
            accessorKey: 'pricePerSf',
            id: 'pricePerSf',
            header: () => <span>{`$/${areaType}/mo.`}</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'pricePerSf');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.totalPricePerSf || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_SF_PER_YEAR]: {
            accessorKey: 'pricePerSfPerYear',
            id: 'pricePerSfPerYear',
            header: () => <span>{`$/${areaType}/year`}</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'pricePerSfPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.totalPricePerSfPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_UNIT]: {
            accessorKey: 'pricePerUnit',
            id: 'pricePerUnit',
            header: () => <span>$/unit/mo.</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'pricePerUnit');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.totalPricePerUnit || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_KW_PER_YEAR]: wattsType === WattsType.MEGAWATTS ? {
            accessorKey: 'valuePerMwPerYear',
            id: 'valuePerMwPerYear',
            header: () => <span>$/MW/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'valuePerMwPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.totalValuePerMwPerYear || 0, true, 'currency')}</span>,
        } : {
            accessorKey: 'valuePerKwPerYear',
            id: 'valuePerKwPerYear',
            header: () => <span>$/kW/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'valuePerKwPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.totalValuePerKwPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_RACK_PER_YEAR]: {
            accessorKey: 'valuePerRackPerYear',
            id: 'valuePerRackPerYear',
            header: () => <span>$/rack/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'valuePerRackPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.totalOtherIncome.totalValuePerRackPerYear || 0, true, 'currency')}</span>,
        },
    };

    const otherIncomeColumns: ColumnDef<OtherIncome>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <span>Other income</span>
                    <FontAwesomeIcon
                        id="plus-icon"
                        icon={faPlus}
                        className="ml-2 text-muted cursor--pointer"
                        onClick={() => {
                            // Auto-set start date for new other income to Operation start (if available)
                            const startDatePayload = operatingInfo?.operationStartDate
                                ? { startDate: operatingInfo.operationStartDate }
                                : undefined;
                            handleCreateOperationsRow(
                                OPERATIONS_INCOME_WS_HANDLERS.OTHER_INCOME.CREATE,
                                startDatePayload,
                            );
                        }}
                    />
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <div className="w-100">
                        <NSCellInput
                            type="text"
                            value={getValue()}
                            handleUpdateValue={updatedValue => {
                                const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'name');
                                if (typeof updateFn === 'function') {
                                    updateFn({
                                        target: { value: updatedValue },
                                    } as React.ChangeEvent<HTMLInputElement>);
                                }
                            }}
                            placeholder="Enter name"
                        />
                    </div>
                    <FontAwesomeIcon
                        id="trash-icon"
                        icon={faTrash}
                        className="ml-1 text-dark cursor--pointer"
                        onClick={() => handleDeleteOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.OTHER_INCOME.DELETE, Number(row.original.id))}
                    />
                </div>
            ),
            footer: () => <span className="px-1">Total other income</span>,
        },
        ...COLUMN_KEYS_BY_MODEL_ID[proForma.proFormaModelId].map(key => COLUMNS_BY_KEY[key]),
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => (
                <div className="pr-1">
                    <NSCellInput
                        showCents={showCents}
                        type="number"
                        numberType="currency"
                        value={getValue()}
                        handleUpdateValue={updatedValue => {
                            const updateFn = handleUpdateOtherIncome(Number(row.original.id), 'amountPerYear');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value: updatedValue },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isRightAligned
                        placeholder="Enter amount"
                    />
                </div>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(incomeSummary.totalOtherIncome.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...otherIncomeCashFlowColumns,
    ];

    const table = useReactTable({
        data: incomeSummary.otherIncome || [],
        columns: otherIncomeColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className={`NSTable OtherIncomeTable--${proForma.proFormaModelName.replace(/ /g, '-')} mb-1`}>
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                <RenderIf isTrue={table.getRowModel().rows.length}>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </RenderIf>
                <RenderIf isTrue={!table.getRowModel().rows.length}>
                    <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        <td colSpan={6} className="NSTable__tbody__tr__td text-center text-muted py-4">
                            <div className="text-dark">Your added income will appear here.</div>
                            <div className="small">
                                Click the
                                {' '}
                                <span className="font-weight-bold text-dark">[+]</span>
                                {' '}
                                symbol in the
                                {' '}
                                <span className="font-weight-bold text-dark">Other income</span>
                                {' '}
                                header to add your first income.
                            </div>
                        </td>
                        {baseCashFlowColumns.map((column, index) => (
                            <td
                                key={`other-income-period-${index}`}
                                className="NSTable__tbody__tr__td"
                                id={`other-income-placeholder-period-${index}`}
                            >
                                <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                    <UncontrolledTooltip target="other-income-placeholder-period-0">
                                        {CASH_FLOW_TOOLTIP_MESSAGE}
                                    </UncontrolledTooltip>
                                </RenderIf>
                            </td>
                        ))}
                    </tr>
                </RenderIf>
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default OtherIncomeTable;
